import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useDispatch } from 'react-redux';
import AppLogoUrl from '@/assets/images/logo/full_logo.svg';
import { isRunningInWTN } from '@/utils';
import { usePointerActivation } from '@/hooks';
import { setAppInstallBannerStatus } from '@/store/slices';
import { APP_STORE_URLS } from '@/constants';

const DISMISS_KEY = 'appSmartBannerDismissed';
const DISMISS_DURATION_MS = 10 * 60 * 1000; // 10 minutes

const IOS_APP_STORE_URL = APP_STORE_URLS.ios;
const ANDROID_PLAY_STORE_URL = APP_STORE_URLS.android;

/**
 * Detect if the user is on a mobile device (actual device, not just viewport).
 */
function isMobileDevice(): boolean {
	if (typeof navigator === 'undefined') return false;
	return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

type Platform = 'ios' | 'android' | 'unknown';

/**
 * Detect iOS vs Android.
 */
function getPlatform(): Platform {
	if (typeof navigator === 'undefined') return 'unknown';
	const ua = navigator.userAgent;
	if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
	if (/Android/i.test(ua)) return 'android';
	return 'unknown';
}

/**
 * Check if the banner was previously dismissed and still within dismiss window.
 */
function isDismissed(): boolean {
	try {
		const val = localStorage.getItem(DISMISS_KEY);
		if (!val) return false;
		const ts = parseInt(val, 10);
		if (Date.now() - ts < DISMISS_DURATION_MS) return true;
		localStorage.removeItem(DISMISS_KEY);
		return false;
	} catch {
		return false;
	}
}

function saveDismissed(): void {
	try {
		localStorage.setItem(DISMISS_KEY, String(Date.now()));
	} catch {
		// ignore
	}
}

/**
 * Custom Smart Banner that works in in-app browsers (Telegram, Instagram, etc.)
 * where the native Apple Smart Banner does not appear.
 *
 * - iOS: attempts Universal Link, falls back to App Store
 * - Android: attempts intent:// scheme, falls back to Play Store
 */
export default function AppSmartBanner(): React.ReactElement | null {
	const dispatch = useDispatch();
	const [visible, setVisible] = useState(false);

	// Publishes what the screen looks like to features that must not be covered by this
	// banner; the store starts at `pending` until this effect resolves.
	useEffect(() => {
		if (!isMobileDevice() || getPlatform() === 'unknown' || isDismissed()) {
			dispatch(setAppInstallBannerStatus('gone'));
			return;
		}

		let cancelled = false;

		// Don't show the banner if already running inside the native app
		isRunningInWTN()
			.then((isWTN) => {
				if (cancelled) return;
				if (isWTN) {
					dispatch(setAppInstallBannerStatus('gone'));
					return;
				}
				setVisible(true);
				dispatch(setAppInstallBannerStatus('visible'));
			})
			.catch(() => {
				// A failed probe must not leave the status stuck at `pending`.
				if (!cancelled) dispatch(setAppInstallBannerStatus('gone'));
			});

		return () => {
			cancelled = true;
			dispatch(setAppInstallBannerStatus('gone'));
		};
	}, [dispatch]);

	const handleDismiss = useCallback(() => {
		saveDismissed();
		setVisible(false);
		dispatch(setAppInstallBannerStatus('gone'));
	}, [dispatch]);

	const handleOpen = useCallback(() => {
		const platform = getPlatform();

		if (platform === 'ios') {
			// Custom URL schemes ARE passed through by Telegram's WKWebView to iOS,
			// unlike universal links which Telegram intercepts and handles internally.
			// webToNative registers the reversed bundle ID: sportspredict.com → com.sportspredict
			// Note: URL schemes cannot contain dots, so the scheme is 'comsportspredict'
			const customSchemeUrl = `${(import.meta as any).env.VITE_SCHEMA_PREFIX}.${window.location.href}`;

			// If the app is installed, iOS will open it via the custom scheme.
			// If not installed, iOS will silently fail — so we fall back to App Store.
			const fallbackTimer = setTimeout(() => {
				window.location.href = IOS_APP_STORE_URL;
			}, 3500);

			const onVisChange = () => {
				if (document.hidden) {
					// App opened successfully — cancel the App Store redirect
					clearTimeout(fallbackTimer);
					document.removeEventListener('visibilitychange', onVisChange);
				}
			};
			document.addEventListener('visibilitychange', onVisChange);

			window.location.href = customSchemeUrl;
		} else if (platform === 'android') {
			const path = window.location.pathname + window.location.search;
			const intentUrl = `intent://${
				window.location.host
			}${path}#Intent;scheme=https;package=com.sportspredict;S.browser_fallback_url=${encodeURIComponent(
				ANDROID_PLAY_STORE_URL
			)};end`;
			window.location.href = intentUrl;
		}
	}, []);

	const openPointerProps = usePointerActivation(handleOpen);

	if (!visible || typeof document === 'undefined') return null;

	// Escape the page's isolated stacking context so fixed headers cannot cover the banner.
	return createPortal(
		<div
			id="app-smart-banner"
			className="md:hidden fixed top-0 left-0 right-0 z-999999 flex items-center gap-2.5 p-3 font-dmsans shadow-[0_4px_20px_rgba(0,0,0,0.5)] animate-[smartBannerSlideIn_0.3s_ease-out]"
			style={{
				background: 'linear-gradient(135deg, #0f1629 0%, #1a2240 100%)',
				paddingTop: 'max(10px, env(safe-area-inset-top))',
				paddingBottom: '10px',
			}}
		>
			<style>{`
        @keyframes smartBannerSlideIn {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
      `}</style>

			{/* Close button */}
			{/* A plain click, not the fast-tap pointerdown: removing the banner on pointerdown
			    lets the browser deliver the tap's click to whatever sat underneath it. */}
			<button
				type="button"
				onClick={handleDismiss}
				aria-label="Close banner"
				className="flex items-center justify-center shrink-0 p-1 bg-transparent border-0 text-[#8892b0] cursor-pointer"
			>
				<X size={18} />
			</button>

			{/* App icon */}
			<div
				className="w-10 h-10 rounded-[10px] flex items-center justify-center shrink-0 overflow-hidden"
				style={{ background: 'linear-gradient(135deg, #1e2748, #2667FF)' }}
			>
				<img src={AppLogoUrl} alt="App Logo" className="w-7 h-7" />
			</div>

			{/* Text */}
			<div className="flex-1 min-w-0">
				<div className="text-white text-sm font-bold leading-[18px] truncate">
					SportsPredict.com
				</div>
				<div className="text-[#8892b0] text-xs leading-4">Open in the app</div>
			</div>

			{/* Open button */}
			<button
				{...openPointerProps}
				className="shrink-0 border-0 rounded-[20px] px-5 py-2 text-[13px] font-bold text-white uppercase cursor-pointer tracking-[0.5px] transition-transform duration-150 ease-in-out active:scale-95 font-dmsans"
				style={{ background: 'linear-gradient(135deg, #2667FF, #1a4fd6)' }}
			>
				OPEN
			</button>
		</div>,
		document.body
	);
}
